import { Component, Inject } from '@angular/core';
import {
  EmailPhoneConfirmationPinDialogComponent,
  EmailPhoneConfirmationPinDialogComponentData,
} from '@libs/ui/email-phone-confirmation-pin-dialog/email-phone-confirmation-pin-dialog.component';
import { filter, takeUntil } from 'rxjs/operators';
import { BaseComponent } from '@libs/index';
import { ErrorHandlerServiceService } from '@libs/index';
import { StateService } from '../../../services/state/state.service';
import { DialogService } from '@libs/index';
import { ResendPinCountdownService } from '@libs/index';
import { UserService } from '@libs/index';
import { FormControl, Validators } from '@angular/forms';
import { User } from '@libs/index';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface ContactUpdateDialogComponentData {
  user: User;
}

@Component({
  selector: 'app-contact-update-dialog',
  templateUrl: './contact-update-dialog.component.html',
  styleUrls: ['./contact-update-dialog.component.scss'],
})
export class ContactUpdateDialogComponent extends BaseComponent {
  public readonly isConfirmEmailProcess: boolean;
  public readonly isConfirmPhoneNrProcess: boolean;

  public readonly user: User;

  public isLoading: boolean;
  public resendPinConfirmCountdownInSecond = 0;

  public emailControl = new FormControl(null, [
    Validators.required,
    Validators.email,
  ]);
  public phoneNrControl = new FormControl(null, [Validators.required]);

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: ContactUpdateDialogComponentData,
    private dialogRef: MatDialogRef<ContactUpdateDialogComponent>,
    private errorHandlerServiceService: ErrorHandlerServiceService,
    private stateService: StateService,
    private dialogService: DialogService,
    private resendPinCountdownService: ResendPinCountdownService,
    private userService: UserService
  ) {
    super();

    this.user = this.data?.user;

    this.isConfirmEmailProcess =
      !this.user?.email || !this.user?.emailConfirmed;
    this.isConfirmPhoneNrProcess =
      !this.user?.phoneNr || !this.user?.phoneNrConfirmed;

    if (!this.isConfirmEmailProcess && !this.isConfirmPhoneNrProcess) {
      // should not happen
      this.closeDialog();
    }

    this.emailControl.setValue(this.user?.email);
    this.phoneNrControl.setValue(this.user?.phoneNr);

    this.resendPinCountdownService.resendPinCountdownInSecond$
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((v) => (this.resendPinConfirmCountdownInSecond = v));
  }

  public isAbleToSubmit(): boolean {
    return this.isAbleToConfirmEmail() || this.isAbleToConfirmPhoneNumber();
  }

  public submit() {
    if (this.isConfirmEmailProcess) {
      this.confirmEmail();
    } else if (this.isConfirmPhoneNrProcess) {
      this.confirmPhoneNumber();
    }
  }

  private isAbleToConfirmEmail(): boolean {
    return (
      !this.isLoading &&
      this.isConfirmEmailProcess &&
      !!this.user &&
      !this.user.emailConfirmed &&
      (!!this.user?.email || this.emailControl.valid) &&
      this.resendPinConfirmCountdownInSecond <= 0
    );
  }

  private async confirmEmail() {
    if (!this.isAbleToConfirmEmail()) {
      return;
    }

    try {
      this.isLoading = true;

      const isUpdateEmail =
        !this.user.email || this.user.email !== this.emailControl.value;

      const response = isUpdateEmail
        ? await this.userService.updateEmailIfNotVerified(
            this.user.id,
            this.emailControl.value
          )
        : await this.userService.getCodeToConfirmEmail(this.user.email);

      if (response?.id) {
        this.resendPinCountdownService.setResendPinCountdownNow();
        const dialogRef = this.dialogService.openDialog(
          EmailPhoneConfirmationPinDialogComponent,
          this.dialogService.createDefaultDialogConfig({
            challengeId: response?.id,
            username: this.emailControl.value,
            isConfirmEmail: true,
          } as EmailPhoneConfirmationPinDialogComponentData)
        );

        dialogRef
          .afterClosed()
          .pipe(filter((isConfirmed) => !!isConfirmed))
          .pipe(takeUntil(this.componentDestroyed))
          .subscribe(() => {
            this.fetchCurrentLoggedInUserAndClose();
          });
      }
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    } finally {
      this.isLoading = false;
    }
  }

  private isAbleToConfirmPhoneNumber(): boolean {
    return (
      !this.isLoading &&
      this.isConfirmPhoneNrProcess &&
      !!this.user &&
      this.phoneNrControl.valid &&
      (!this.user.phoneNrConfirmed ||
        this.user.phoneNr !== this.phoneNrControl.value) &&
      this.resendPinConfirmCountdownInSecond <= 0
    );
  }

  private async confirmPhoneNumber() {
    if (!this.isAbleToConfirmPhoneNumber()) {
      return;
    }

    try {
      this.isLoading = true;

      const isUpdatePhoneNr = this.user.phoneNr !== this.phoneNrControl.value;

      const response = isUpdatePhoneNr
        ? await this.userService.updatePhoneNumber(
            this.user.id,
            this.phoneNrControl.value
          )
        : await this.userService.getCodeToConfirmPhoneNumber(this.user.phoneNr);

      if (response?.id) {
        this.resendPinCountdownService.setResendPinCountdownNow();
        const dialogRef = this.dialogService.openDialog(
          EmailPhoneConfirmationPinDialogComponent,
          this.dialogService.createDefaultDialogConfig({
            challengeId: response?.id,
            username: this.phoneNrControl.value,
            isConfirmEmail: false,
          } as EmailPhoneConfirmationPinDialogComponentData)
        );

        dialogRef
          .afterClosed()
          .pipe(filter((isConfirmed) => !!isConfirmed))
          .pipe(takeUntil(this.componentDestroyed))
          .subscribe(() => this.fetchCurrentLoggedInUserAndClose());
      }
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    } finally {
      this.isLoading = false;
    }
  }

  public closeDialog(): void {
    this.dialogRef.close();
  }

  private async fetchCurrentLoggedInUserAndClose() {
    try {
      const user = await this.userService.getCurrentLoggedInUser();
      this.stateService.setUser(user);
      this.closeDialog();
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    }
  }
}
